import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, ClipboardList, Gavel, Music4, Trophy, ArrowRight, Medal, Newspaper, Mail, Loader2, CheckCircle2, Pencil } from "lucide-react";
import { SiteLogo } from "@/components/SiteLogo";
import { BrandText } from "@/components/BrandText";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionsView, SectionsEditor } from "@/components/SectionBlocks";
import { EditableImage } from "@/components/EditableImage";
import { HeroMedia } from "@/components/HeroMedia";
import { EditableText } from "@/components/EditableText";
import { useSiteTexts } from "@/lib/siteConfig";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ArticleBody from "@/components/ArticleBody";
import { PublicNewsCard } from "@/components/PublicNewsCard";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/18886520/pexels-photo-18886520.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const RITMICA = "https://images.pexels.com/photos/15281444/pexels-photo-15281444.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ARTISTICA = "https://images.pexels.com/photos/6572570/pexels-photo-6572570.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const features = [
  { icon: CalendarDays, title: "Calendario Eventi", desc: "Programma gare e manifestazioni con date, sede e disciplina.", section: "calendario", statKey: "events", unit: "eventi" },
  { icon: ClipboardList, title: "Iscrizioni Atleti", desc: "Raccogli i dati dei partecipanti e le tracce musicali delle esibizioni.", section: "iscrizioni", statKey: "athletes", unit: "atleti iscritti" },
  { icon: Music4, title: "Programma di Lavoro", desc: "Genera l'ordine di esibizione con nomi e musiche pronto per la gara.", section: "programma", statKey: "athletes", unit: "esibizioni" },
  { icon: Gavel, title: "Area Giudici", desc: "Inserimento punteggi con somma automatica di difficoltà ed esecuzione.", section: "giudici", statKey: "scores", unit: "punteggi" },
  { icon: Trophy, title: "Classifiche Live", desc: "Ranking automatici dal punteggio più alto al più basso, per categoria.", section: "classifica", statKey: "scores", unit: "valutazioni" },
];

export default function Landing() {
  const { user } = useAuth();
  const [news, setNews] = useState([]);
  const [stats, setStats] = useState(null);
  const [nl, setNl] = useState({ name: "", email: "" });
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [homeSections, setHomeSections] = useState([]);
  const [homeEdit, setHomeEdit] = useState(false);
  const [homeSaving, setHomeSaving] = useState(false);
  const isAdmin = user?.role === "admin";
  const { texts } = useSiteTexts();
  const t = (slot, def) => (texts[slot] != null && texts[slot] !== "" ? texts[slot] : def);

  useEffect(() => {
    api.get("/news").then((r) => setNews((r.data || []).filter((n) => n.published !== false).slice(0, 3))).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/page-content/home").then((r) => setHomeSections(r.data.sections || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash !== "#newsletter") return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById("newsletter");
      if (el) {
        clearInterval(timer);
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          const input = document.querySelector('[data-testid="newsletter-name-input"]');
          if (input) input.focus({ preventScroll: true });
        }, 600);
      } else if (++tries > 20) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, []);

  const saveHome = async (draft) => {
    setHomeSaving(true);
    try {
      const { data } = await api.put("/page-content/home", { sections: draft });
      setHomeSections(data.sections || draft);
      setHomeEdit(false);
      toast.success("Sezioni Home salvate");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setHomeSaving(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nl.name.trim() || !nl.email.trim()) { toast.error("Inserisci nome ed email"); return; }
    setSubscribing(true);
    try {
      const disciplines = nl.discipline === "entrambe" ? ["artistica", "ritmica"] : [nl.discipline];
      const { data } = await api.post("/newsletter", { name: nl.name.trim(), email: nl.email.trim().toLowerCase(), disciplines });
      localStorage.setItem("gc_newsletter", "1");
      toast.success(data.message || "Iscrizione completata");
      setSubscribed(true);
      setNl({ name: "", email: "", discipline: "entrambe" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubscribing(false);
    }
  };
  return (
    <div className="min-h-screen bg-background text-foreground noise-bg overflow-hidden">
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5">
            <SiteLogo className="w-9 h-9" />
            <div className="flex flex-col leading-tight">
              <BrandText nameClassName="font-display text-2xl font-semibold tracking-tight" />
            </div>
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/pagina/chi-siamo" data-testid="nav-chi-siamo" className="hidden md:inline text-sm text-muted-foreground hover:text-gold transition-colors px-2 py-2">{t("nav_chi_siamo", "Chi Siamo")}</Link>
            <Link to="/pagina/contatti" data-testid="nav-contatti" className="hidden md:inline text-sm text-muted-foreground hover:text-gold transition-colors px-2 py-2">{t("nav_contatti", "Contatti")}</Link>
            <Link to="/documenti" data-testid="nav-documenti" className="hidden md:inline text-sm text-muted-foreground hover:text-gold transition-colors px-2 py-2">Documenti</Link>
            {user ? (
              <>
                {user.role === "judge" && (
                  <Link to="/app?section=giudici" data-testid="nav-judge-area-btn" className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-gold/40 text-gold px-4 py-2 text-sm font-semibold hover:bg-gold/10 transition-colors">
                    <Gavel className="w-4 h-4" /> Area Giudici
                  </Link>
                )}
                <Link to="/app" data-testid="nav-dashboard-btn" className="rounded-full bg-gold text-[#020617] px-5 py-2 text-sm font-semibold hover:bg-[#FDE047] transition-colors">
                  Dashboard
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="text-sm text-muted-foreground hover:text-foreground transition-colors px-3 py-2">
                  Accedi
                </Link>
                <Link to="/register" data-testid="nav-register-btn" className="rounded-full bg-gold text-[#020617] px-5 py-2 text-sm font-semibold hover:bg-[#FDE047] transition-colors">
                  Registrati
                </Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline text-gold uppercase text-xs tracking-[0.25em]"><EditableText slot="home_hero_overline" defaultText="Ginnastica Artistica & Ritmica" /></span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight mt-4">
              <EditableText slot="home_hero_title" defaultText="Risorse che generano" /> <span className="text-gold"><EditableText slot="home_hero_title_accent" defaultText="valore" /></span>
            </h1>
            <p className="text-lg text-muted-foreground mt-6 max-w-md leading-relaxed">
              <EditableText slot="home_hero_desc" defaultText="Trasformiamo l'organizzazione delle competizioni in valore concreto: calendario, iscrizioni, musiche, giudizi e classifiche in un'unica piattaforma elegante, al servizio di atleti, società e giudici." multiline />
            </p>
            <div className="flex items-center gap-4 mt-10">
              <Link to="/register" data-testid="hero-cta-btn" className="group rounded-full bg-gold text-[#020617] px-7 py-3.5 font-semibold flex items-center gap-2 hover:bg-[#FDE047] transition-colors">
                {t("home_hero_cta", "Inizia ora")} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/login" data-testid="hero-secondary-btn" className="rounded-full border border-white/15 px-7 py-3.5 font-medium hover:border-gold/60 hover:text-gold transition-colors">
                {t("home_hero_cta2", "Accedi")}
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.2 }} className="relative">
            <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full" />
            <HeroMedia defaultSrc={HERO} alt="Ginnasta" />
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="relative px-6 py-16 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8">
          {[
            { img: ARTISTICA, tag: "Disciplina", title: "Ginnastica Artistica", desc: "Attrezzi, forza e precisione tecnica.", slug: "artistica" },
            { img: RITMICA, tag: "Disciplina", title: "Ginnastica Ritmica", desc: "Nastro, cerchio, palla ed espressività.", slug: "ritmica" },
          ].map((c, i) => (
            <motion.div key={c.title} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}>
              <Link to={`/disciplina/${c.slug}`} data-testid={`discipline-card-${c.slug}`}
                className="group relative block rounded-2xl overflow-hidden border border-white/10 h-80 hover:border-gold/50 transition-colors">
                <EditableImage slot={`discipline_${c.slug}`} defaultSrc={c.img} alt={c.title} className="absolute inset-0" imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#020617] via-[#020617]/50 to-transparent" />
                <div className="absolute bottom-0 p-8">
                  <span className="overline text-gold uppercase text-xs tracking-[0.25em]"><EditableText slot={`home_disc_${c.slug}_tag`} defaultText={c.tag} /></span>
                  <h3 className="font-display text-3xl mt-2"><EditableText slot={`home_disc_${c.slug}_title`} defaultText={c.title} /></h3>
                  <p className="text-muted-foreground mt-2"><EditableText slot={`home_disc_${c.slug}_desc`} defaultText={c.desc} /></p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gold opacity-0 group-hover:opacity-100 transition-opacity">
                    Scopri la disciplina <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="relative px-6 py-20 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl sm:text-5xl tracking-tight max-w-xl"><EditableText slot="home_features_title" defaultText="Tutto ciò che serve per la competizione" /></h2>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
              <Link
                to={`/app?section=${f.section}`}
                data-testid={`feature-card-${f.section}`}
                className="group block h-full rounded-xl border border-white/10 bg-[#0F172A] p-7 hover:border-gold/50 hover:bg-[#111c33] transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="w-11 h-11 rounded-lg bg-gold/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                    <f.icon className="w-5 h-5 text-gold" />
                  </div>
                  {stats && (
                    <span data-testid={`feature-stat-${f.section}`} className="inline-flex items-center gap-1.5 rounded-full bg-gold/10 border border-gold/20 px-3 py-1 text-xs font-semibold text-gold">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" />
                      {stats[f.statKey] ?? 0} {f.unit}
                    </span>
                  )}
                </div>
                <h4 className="text-xl font-semibold mt-5"><EditableText slot={`home_feature_${f.section}_title`} defaultText={f.title} /></h4>
                <p className="text-muted-foreground mt-2 leading-relaxed"><EditableText slot={`home_feature_${f.section}_desc`} defaultText={f.desc} /></p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-gold opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all">
                  Vai alla sezione <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Notizie */}
      <section id="notizie" className="relative px-6 py-20 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="overline text-gold uppercase text-xs tracking-[0.25em]"><EditableText slot="home_news_overline" defaultText="Aggiornamenti" /></span>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-3"><EditableText slot="home_news_title" defaultText="Notizie & Eventi" /></h2>
          </div>
          <Link to="/notizie" data-testid="all-news-link" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gold hover:translate-x-0.5 transition-transform shrink-0">Tutte le notizie <ArrowRight className="w-4 h-4" /></Link>
        </div>
        {news.length === 0 ? (
          <div className="text-center py-14 text-muted-foreground border border-dashed border-white/10 rounded-xl" data-testid="landing-news-empty">Nessuna notizia al momento. Torna presto!</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {news.map((item, i) => (
              <motion.div key={item.id} data-testid={`landing-news-${item.id}`} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }}>
                <PublicNewsCard item={item} />
              </motion.div>
            ))}
          </div>
        )}
        {news.length > 0 && (
          <div className="sm:hidden mt-8 text-center">
            <Link to="/notizie" className="inline-flex items-center gap-1.5 text-sm font-medium text-gold">Tutte le notizie <ArrowRight className="w-4 h-4" /></Link>
          </div>
        )}
      </section>

      {(homeSections.length > 0 || isAdmin) && (
        <section id="home-sections" data-testid="home-custom-sections" className="relative px-6 py-16 max-w-5xl mx-auto">
          {isAdmin && !homeEdit && (
            <div className="flex justify-end mb-6">
              <Button data-testid="edit-home-btn" size="sm" onClick={() => setHomeEdit(true)} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifica sezioni Home
              </Button>
            </div>
          )}
          {homeEdit ? (
            <SectionsEditor initial={homeSections} onSave={saveHome} onCancel={() => setHomeEdit(false)} saving={homeSaving} />
          ) : homeSections.length > 0 ? (
            <SectionsView sections={homeSections} />
          ) : (
            <div className="text-center py-10 text-muted-foreground border border-dashed border-white/10 rounded-xl">
              Nessuna sezione personalizzata. Aggiungine una con "Modifica sezioni Home".
            </div>
          )}
        </section>
      )}

      {/* Newsletter */}
      <section id="newsletter" className="relative px-6 py-20">
        <div className="max-w-4xl mx-auto rounded-3xl border border-gold/20 bg-gradient-to-b from-gold/5 to-transparent p-10 sm:p-14 text-center gold-glow">
          <div className="w-14 h-14 rounded-2xl bg-gold/15 flex items-center justify-center mx-auto">
            <Mail className="w-7 h-7 text-gold" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight mt-6"><EditableText slot="home_newsletter_title" defaultText="Resta aggiornato" /></h2>
          <p className="text-muted-foreground mt-4 max-w-lg mx-auto"><EditableText slot="home_newsletter_desc" defaultText="Iscriviti alla newsletter per ricevere le novità su gare ed eventi organizzati. Ti basta inserire nome ed email." multiline /></p>
          {subscribed ? (
            <div className="mt-8 inline-flex items-center gap-2 text-gold" data-testid="newsletter-success">
              <CheckCircle2 className="w-5 h-5" /> Iscrizione completata! Grazie.
            </div>
          ) : (
            <form onSubmit={subscribe} className="mt-8 max-w-xl mx-auto space-y-3">
              <div className="flex flex-col sm:flex-row gap-3">
                <Input data-testid="newsletter-name-input" value={nl.name} onChange={(e) => setNl({ ...nl, name: e.target.value })} placeholder="Nome e cognome" className="flex-1" />
                <Input data-testid="newsletter-email-input" type="email" value={nl.email} onChange={(e) => setNl({ ...nl, email: e.target.value })} placeholder="La tua email" className="flex-1" />
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                <Select value={nl.discipline} onValueChange={(v) => setNl({ ...nl, discipline: v })}>
                  <SelectTrigger data-testid="newsletter-discipline-select" className="flex-1 text-left"><SelectValue placeholder="Cosa vuoi ricevere?" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="entrambe" data-testid="newsletter-discipline-entrambe">Entrambe le discipline</SelectItem>
                    <SelectItem value="artistica" data-testid="newsletter-discipline-artistica">Solo Ginnastica Artistica</SelectItem>
                    <SelectItem value="ritmica" data-testid="newsletter-discipline-ritmica">Solo Ginnastica Ritmica</SelectItem>
                  </SelectContent>
                </Select>
                <Button data-testid="newsletter-submit-btn" type="submit" disabled={subscribing} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold sm:w-40">
                  {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Iscriviti"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <footer className="relative border-t border-white/10 mt-10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <SiteLogo className="w-7 h-7" />
            <div className="flex flex-col leading-tight">
              <BrandText nameClassName="font-display text-xl" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground"><EditableText slot="footer_copyright" defaultText="© 2026 OPES Settore Ginnastiche — Ginnastica Artistica & Ritmica" /></p>
        </div>
      </footer>
    </div>
  );
}
